import { Shop } from '@shared/components/ui/shop';
import { cn } from '@shared/utils/utils';
import { faCalendar, faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Order } from '@modules/OrderPreparation/PackingV2/models/Packing';
import { useTranslation } from 'react-i18next';
import { Badge } from '@shared/components/ui/badge';
import { isOrderOnlyDiscrepancyProduct } from '../../utils/computeSmartOrderSelection';
import { getOrderAllProductSummaries, getOrderProductQtyTotal } from '../../utils/orderHasProduct';

interface AdjustDiscrepancyOrderCardProps {
  order: Order;
  productId: number;
  selected: boolean;
  onToggle: (selected: boolean) => void;
}

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const AdjustDiscrepancyOrderCard = ({
  order,
  productId,
  selected,
  onToggle,
}: AdjustDiscrepancyOrderCardProps) => {
  const { t } = useTranslation('pickingModule');
  const products = getOrderAllProductSummaries(order);
  const productQtyInOrder = getOrderProductQtyTotal(order, productId);
  const isSingleProductOrder = isOrderOnlyDiscrepancyProduct(order, productId);

  return (
    <button
      type="button"
      onClick={() => onToggle(!selected)}
      data-testid={`adjust-discrepancy-order-card-${order.id}`}
      className={cn(
        'w-full text-left rounded-md border px-3 py-2 transition-colors',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-primary bg-primary/5' : 'border-border bg-card',
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background',
          )}
          aria-hidden
        >
          {selected ? <FontAwesomeIcon icon={faCheck} className="w-3 h-3" /> : null}
        </div>

        <span className="text-sm font-semibold text-foreground shrink-0">{order.ref}</span>

        {isSingleProductOrder && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
            {t('adjust_discrepancy.order_card.single_product')}
          </Badge>
        )}

        <span className="text-border shrink-0">·</span>

        <div className="min-w-0 flex-1">
          {order.shop ? (
            <Shop name={order.shop.name} src={order.shop.image_hash ?? ''} className="py-0 gap-1.5" />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        <span className="text-border shrink-0 hidden sm:inline">·</span>

        <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
          {formatDate(order.order_created_at)}
        </span>

        <span
          className={cn('text-xs font-semibold shrink-0 tabular-nums', selected ? 'text-primary' : 'text-foreground')}
        >
          {t('adjust_discrepancy.order_card.product_qty', { qty: productQtyInOrder })}
        </span>
      </div>

      {products.length > 0 && (
        <p className="mt-1.5 pl-8 text-xs text-muted-foreground line-clamp-2">
          {products.map((p, i) => (
            <span key={`${order.id}-${p.product_id}-${i}`}>
              {i > 0 ? ' · ' : ''}
              <span className="text-foreground">{p.name}</span>
              <span className="text-muted-foreground"> ×{p.qty}</span>
            </span>
          ))}
        </p>
      )}
    </button>
  );
};
